/* -----------------------------------------------------------------------
 * Creator Setup — entry page for the Nonogram Creator
 *
 * Lets the user pick an initial size or load a past creation before
 * entering the editor. Emits a CreatorInit payload via onStart.
 * ----------------------------------------------------------------------- */

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import type { LevelData, PuzzleDefinition, Category } from '../types';
import { ScreenLayout } from './ui/Panel';
import { SettingsChoiceRow } from './ui/SettingsMenu';
import { Caption, Muted } from './ui/Text';
import { Button } from './ui/Button';
import { ConfirmPopup } from './ui/Popups';
import { LevelCardGrid } from './LevelCardGrid';
import creatorLogo from '../../assets/creator_logo.png';
import './Creator.css';

export type CreatorInit =
  | { kind: 'new'; size: number; multi: boolean }
  | { kind: 'remix'; level: LevelData; multi: boolean };

interface CreatorSetupProps {
  onStart: (init: CreatorInit) => void;
}

const ENTRY_CARDS_PER_PAGE = 6; // 2 rows x 3 cards
const PAST_LEVEL_STORAGE_PREFIX = 'nonogram_creator_';

const NEW_SIZES: { size: number; meta: string }[] = [
  { size: 5, meta: 'Tiny' },
  { size: 10, meta: 'Small' },
  { size: 15, meta: 'Classic' },
  { size: 20, meta: 'Medium' },
  { size: 25, meta: 'Large' },
  { size: 30, meta: 'Huge' },
];

interface StoredCreation {
  name?: string;
  rows?: number;
  cols?: number;
  solution?: unknown;
  puzzles?: PuzzleDefinition[];
}

/** Normalize a stored solution (either `string[]` with '0'/'1' chars,
 *  or `number[][]` / `boolean[][]`) to a rectangular `boolean[][]`. */
export function normalizeStoredSolution(
  raw: unknown,
  rowsHint?: number,
  colsHint?: number,
): boolean[][] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const first = raw[0];

  let parsed: boolean[][];
  if (typeof first === 'string') {
    parsed = (raw as string[]).map((row) => [...row].map((c) => c === '1'));
  } else if (Array.isArray(first)) {
    parsed = (raw as unknown[][]).map((row) => row.map((c) => Boolean(c)));
  } else {
    return null;
  }

  const rows = rowsHint ?? parsed.length;
  const cols = colsHint ?? parsed[0]?.length ?? 0;
  if (rows <= 0 || cols <= 0) return null;

  return Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => Boolean(parsed[r]?.[c])),
  );
}

/** Parse an uploaded JSON file into a LevelData. Accepts both the
 *  creator export format (`solution: number[][]` + `rows`/`cols`) and the
 *  canonical campaign format (`solution: string[]`). */
function parseUploadedLevel(raw: unknown): LevelData {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid JSON: not an object');
  }
  const obj = raw as Record<string, unknown>;
  const name = typeof obj.name === 'string' && obj.name ? obj.name : 'Untitled';
  const id = typeof obj.id === 'string' && obj.id ? obj.id : `upload_${Date.now()}`;

  const rawSolution = obj.solution;
  let solution: boolean[][];
  if (Array.isArray(rawSolution) && rawSolution.length > 0) {
    const first = rawSolution[0];
    if (typeof first === 'string') {
      solution = (rawSolution as string[]).map((row) =>
        [...row].map((c) => c === '1'),
      );
    } else if (Array.isArray(first)) {
      solution = (rawSolution as unknown[][]).map((row) =>
        row.map((c) => Boolean(c)),
      );
    } else {
      throw new Error('Invalid solution format');
    }
  } else {
    throw new Error('Missing or empty solution');
  }

  const rows = solution.length;
  const cols = solution[0]?.length ?? 0;
  if (rows <= 0 || cols <= 0) throw new Error('Invalid solution dimensions');

  // Normalize all rows to the same width.
  solution = solution.map((row) =>
    Array.from({ length: cols }, (_, c) => Boolean(row[c])),
  );

  const rawPuzzles = Array.isArray(obj.puzzles) ? (obj.puzzles as Record<string, unknown>[]) : [];
  const puzzles: PuzzleDefinition[] = rawPuzzles.length
    ? rawPuzzles.map((p, idx) => ({
        id: typeof p.id === 'string' ? p.id : `${idx}`,
        row: Number(p.row) || 0,
        col: Number(p.col) || 0,
        width: Number(p.width) || cols,
        height: Number(p.height) || rows,
        difficulty: Number((p as { difficulty?: number; difficultyScore?: number }).difficulty
          ?? (p as { difficultyScore?: number }).difficultyScore
          ?? 0),
      }))
    : [{ id: '0_0', row: 0, col: 0, width: cols, height: rows, difficulty: 0 }];

  const category: Category = (typeof obj.category === 'string' ? obj.category : 'community') as Category;

  return { id, name, category, solution, puzzles };
}

/** Load past creations from localStorage, newest first. */
function loadPastCreations(): LevelData[] {
  const entries: { key: string; data: StoredCreation; savedAt: number }[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(PAST_LEVEL_STORAGE_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const data = JSON.parse(raw) as StoredCreation;
        const savedAt = Number(key.slice(PAST_LEVEL_STORAGE_PREFIX.length)) || 0;
        entries.push({ key, data, savedAt });
      } catch {
        // Skip corrupt entries.
      }
    }
  } catch {
    return [];
  }

  entries.sort((a, b) => b.savedAt - a.savedAt);

  const levels: LevelData[] = [];
  for (const { key, data } of entries) {
    const boolSolution = normalizeStoredSolution(data.solution, data.rows, data.cols);
    if (!boolSolution) continue;
    const rows = boolSolution.length;
    const cols = boolSolution[0]?.length ?? 0;
    if (rows <= 0 || cols <= 0) continue;
    const puzzles: PuzzleDefinition[] =
      Array.isArray(data.puzzles) && data.puzzles.length > 0
        ? data.puzzles.map((p) => ({ ...p }))
        : [{ id: '0_0', row: 0, col: 0, width: cols, height: rows, difficulty: 0 }];
    levels.push({
      id: key,
      name: data.name || 'Untitled',
      category: 'community' as Category,
      solution: boolSolution,
      puzzles,
    });
  }
  return levels;
}

export function CreatorSetup({ onStart }: CreatorSetupProps) {
  const [multiPuzzleEnabled, setMultiPuzzleEnabled] = useState(false);
  const entryScrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [entryPage, setEntryPage] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [pastLevels, setPastLevels] = useState<LevelData[]>(() => loadPastCreations());
  const [deleteTarget, setDeleteTarget] = useState<LevelData | null>(null);

  const entryPages = useMemo(() => {
    const result: LevelData[][] = [];
    for (let i = 0; i < pastLevels.length; i += ENTRY_CARDS_PER_PAGE) {
      result.push(pastLevels.slice(i, i + ENTRY_CARDS_PER_PAGE));
    }
    return result;
  }, [pastLevels]);

  const entryTotalPages = Math.max(1, entryPages.length);
  const hasPastLevels = pastLevels.length > 0;

  useEffect(() => {
    if (entryPage >= entryTotalPages) setEntryPage(entryTotalPages - 1);
  }, [entryPage, entryTotalPages]);

  const scrollEntryToPage = useCallback((p: number) => {
    const el = entryScrollRef.current;
    if (!el) return;
    el.scrollTo({ left: p * el.clientWidth, behavior: 'smooth' });
    setEntryPage(p);
  }, []);

  const handleEntryScroll = useCallback(() => {
    const el = entryScrollRef.current;
    if (!el || el.clientWidth === 0) return;
    const newPage = Math.round(el.scrollLeft / el.clientWidth);
    setEntryPage(Math.max(0, Math.min(newPage, entryTotalPages - 1)));
  }, [entryTotalPages]);

  const handleCreateNew = useCallback(
    (size: number) => {
      onStart({ kind: 'new', size, multi: multiPuzzleEnabled });
    },
    [multiPuzzleEnabled, onStart],
  );

  const handleUploadClick = useCallback(() => {
    setUploadError(null);
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset so the same file can be chosen again after an error.
      event.target.value = '';
      if (!file) return;
      try {
        const text = await file.text();
        const json = JSON.parse(text);
        const level = parseUploadedLevel(json);
        const multi = multiPuzzleEnabled || level.puzzles.length > 1;
        onStart({ kind: 'remix', level, multi });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to read file';
        setUploadError(msg);
      }
    },
    [multiPuzzleEnabled, onStart],
  );

  const handlePickPastLevel = useCallback(
    (level: LevelData) => {
      const multi = multiPuzzleEnabled || level.puzzles.length > 1;
      onStart({ kind: 'remix', level, multi });
    },
    [multiPuzzleEnabled, onStart],
  );

  const handleRequestDelete = useCallback((level: LevelData) => {
    setDeleteTarget(level);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    try {
      localStorage.removeItem(deleteTarget.id);
    } catch {
      // ignore storage errors
    }
    setPastLevels((prev) => prev.filter((l) => l.id !== deleteTarget.id));
    setDeleteTarget(null);
  }, [deleteTarget]);

  const handleCancelDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  return (
    <ScreenLayout>
      <div className="creator cr-entry">
        <img
          className="cr-entry__logo"
          src={creatorLogo}
          alt="Nonogram Creator"
        />

        <div className="cr-entry__mode-bar">
          <SettingsChoiceRow
            className="cr-entry__mode-row"
            label="Puzzle mode"
            description="Multi-nonogram puzzles"
            options={[
              { value: 'single' as const, label: 'Single' },
              { value: 'multi' as const, label: 'Multi' },
            ]}
            value={multiPuzzleEnabled ? 'multi' : 'single'}
            onChange={(v) => setMultiPuzzleEnabled(v === 'multi')}
          />
          <Button
            variant="ghost"
            size="custom"
            className="cr-entry__upload-icon"
            onClick={handleUploadClick}
            aria-label="Upload JSON"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M10 17V7M10 7L6 11M10 7L14 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 5V4C4 3.44772 4.44772 3 5 3H15C15.5523 3 16 3.44772 16 4V5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="cr-entry__file-input"
            onChange={handleFileChange}
          />
        </div>
        {uploadError && (
          <Caption className="cr-entry__upload-error">{uploadError}</Caption>
        )}

        <div className="cr-entry__section">
          <Caption className="cr-entry__section-label">Create from scratch</Caption>
          <div className="cr-entry__buttons">
            {NEW_SIZES.map(({ size, meta }) => (
              <Button
                key={size}
                variant="primary"
                size="square"
                className="cr-entry__size-btn"
                onClick={() => handleCreateNew(size)}
                aria-label={`Create new ${size} by ${size} puzzle`}
              >
                <span className="cr-entry__size-label">{size}×{size}</span>
                <Muted className="cr-entry__size-meta">{meta}</Muted>
              </Button>
            ))}
          </div>
        </div>

        {hasPastLevels && (
          <div className="cr-entry__carousel-section">
            <Caption className="cr-entry__section-label">Your past creations</Caption>
            <div
              className="cr-entry__carousel"
              ref={entryScrollRef}
              onScroll={handleEntryScroll}
            >
              {entryPages.map((pageLevels, pi) => (
                <div className="cr-entry__carousel-page" key={pi}>
                  {pageLevels.map((level) => {
                    const allCells = new Set<number>(
                      level.puzzles.map((_, i) => i),
                    );
                    return (
                      <div
                        key={level.id}
                        className="cr-entry__card-wrap cr-entry__level-card"
                        role="button"
                        tabIndex={0}
                        onClick={() => handlePickPastLevel(level)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePickPastLevel(level);
                          }
                        }}
                      >
                        <LevelCardGrid
                          level={level}
                          state="completed"
                          revealedCells={allCells}
                          showSubLevels={false}
                        />
                        <button
                          type="button"
                          className="cr-entry__card-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRequestDelete(level);
                          }}
                          aria-label={`Delete ${level.name}`}
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                            <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {entryTotalPages > 1 && (
              <div className="cr-entry__pagination">
                <button
                  type="button"
                  className="cr-entry__page-arrow"
                  onClick={() => scrollEntryToPage(Math.max(0, entryPage - 1))}
                  disabled={entryPage === 0}
                  aria-label="Previous page"
                >
                  {'\u2039'}
                </button>
                <Caption className="cr-entry__page-indicator">
                  {entryPage + 1} / {entryTotalPages}
                </Caption>
                <button
                  type="button"
                  className="cr-entry__page-arrow"
                  onClick={() => scrollEntryToPage(Math.min(entryTotalPages - 1, entryPage + 1))}
                  disabled={entryPage === entryTotalPages - 1}
                  aria-label="Next page"
                >
                  {'\u203A'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {deleteTarget && (
        <ConfirmPopup
          title="Delete creation?"
          message={`"${deleteTarget.name}" will be permanently removed.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          destructive
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </ScreenLayout>
  );
}

export default CreatorSetup;
